import { useState, useEffect, useCallback } from 'react';
import { Printer, AlertTriangle, ExternalLink, RefreshCw, Check, X, Loader2 } from 'lucide-react';
import {
  isDesktop,
  getPrinters,
  getSavedPrinter,
  savePrinter,
  printAssetLabelPC,
  printLocationLabelPC,
  getPrintServerDownloadUrl,
  type PrinterInfo,
  type AssetPrintData,
  type LocationPrintData,
  type PrintResult,
} from '../lib/pcPrint';

interface PCPrintButtonProps {
  type: 'asset' | 'location';
  data: AssetPrintData | LocationPrintData;
  className?: string;
  label?: string;
}

type ModalState =
  | { step: 'closed' }
  | { step: 'loading' }
  | { step: 'no-service' }
  | { step: 'select-printer'; printers: PrinterInfo[] }
  | { step: 'printing' }
  | { step: 'success'; message: string }
  | { step: 'error'; message: string };

/**
 * Button + modal for printing labels via MakeID Label Print Server (desktop only).
 * Handles the entire flow: check service → select printer → print → show result.
 */
export default function PCPrintButton({ type, data, className, label }: PCPrintButtonProps) {
  const [modal, setModal] = useState<ModalState>({ step: 'closed' });
  const [selectedPrinter, setSelectedPrinter] = useState<string>('');

  // Don't render on mobile
  if (!isDesktop()) return null;

  const handleClick = async () => {
    setModal({ step: 'loading' });

    try {
      const printers = await getPrinters();
      if (printers.length === 0) {
        setModal({ step: 'no-service' });
        return;
      }

      // If we have a saved printer that's still available, use it directly
      const saved = getSavedPrinter();
      const savedPrinterInfo = saved ? printers.find(p => p.printer === saved) : null;

      if (savedPrinterInfo) {
        // Print directly with saved printer
        await doPrint(buildPrinterString(savedPrinterInfo));
      } else if (printers.length === 1) {
        // Only one printer, use it directly
        const printerStr = buildPrinterString(printers[0]);
        savePrinter(printers[0].printer);
        await doPrint(printerStr);
      } else {
        // Multiple printers, show selection
        setSelectedPrinter(printers[0].printer);
        setModal({ step: 'select-printer', printers });
      }
    } catch {
      setModal({ step: 'no-service' });
    }
  };

  const buildPrinterString = (printer: PrinterInfo): string => {
    // The SDK's SetPrinter expects just the printer name.
    // It internally queries the print server and builds the full
    // "name&&dots&&hasDrive&&USBID" string via GetRightPrinter.
    return printer.printer;
  };

  const doPrint = async (printerString: string) => {
    setModal({ step: 'printing' });

    try {
      let result: PrintResult;

      if (type === 'asset') {
        result = await printAssetLabelPC(data as AssetPrintData, printerString);
      } else {
        result = await printLocationLabelPC(data as LocationPrintData, printerString);
      }

      if (result.success) {
        setModal({ step: 'success', message: result.errMsg });
        // Auto-close after 2 seconds
        setTimeout(() => setModal({ step: 'closed' }), 2000);
      } else {
        setModal({ step: 'error', message: result.errMsg });
      }
    } catch (err: any) {
      setModal({ step: 'error', message: err?.message || 'Errore di stampa sconosciuto' });
    }
  };

  const handlePrinterConfirm = async (printers: PrinterInfo[]) => {
    const printer = printers.find(p => p.printer === selectedPrinter);
    if (!printer) return;

    savePrinter(printer.printer);
    await doPrint(buildPrinterString(printer));
  };

  const closeModal = () => setModal({ step: 'closed' });

  return (
    <>
      <button
        onClick={handleClick}
        className={className || 'btn btn-primary text-sm flex-1 flex items-center justify-center gap-2'}
      >
        <Printer className="w-4 h-4" />
        {label || 'Stampa Etichetta'}
      </button>

      {/* Modal overlay */}
      {modal.step !== 'closed' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={closeModal}>
          <div
            className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Loading */}
            {modal.step === 'loading' && (
              <div className="flex flex-col items-center gap-4 py-6">
                <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
                <p className="text-gray-600">Connessione al server di stampa...</p>
              </div>
            )}

            {/* No Service */}
            {modal.step === 'no-service' && (
              <div className="flex flex-col items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-amber-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Server di stampa non trovato</h3>
                <p className="text-sm text-gray-600 text-center">
                  Per stampare le etichette dal PC è necessario installare e avviare il
                  <strong> MakeID Label Print Server</strong>.
                </p>
                <div className="bg-gray-50 rounded-lg p-4 w-full">
                  <p className="text-xs text-gray-500 mb-2">Come procedere:</p>
                  <ol className="text-xs text-gray-600 space-y-1 list-decimal list-inside">
                    <li>Scarica il software dal sito MakeID</li>
                    <li>Installa e avvia il servizio</li>
                    <li>Collega la stampante MakeID via USB</li>
                    <li>Torna qui e riprova</li>
                  </ol>
                </div>
                <div className="flex gap-2 w-full">
                  <a
                    href={getPrintServerDownloadUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary text-sm flex-1 flex items-center justify-center gap-2"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Scarica
                  </a>
                  <button
                    onClick={handleClick}
                    className="btn btn-secondary text-sm flex-1 flex items-center justify-center gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Riprova
                  </button>
                </div>
                <button onClick={closeModal} className="text-xs text-gray-400 hover:text-gray-600 mt-1">
                  Chiudi
                </button>
              </div>
            )}

            {/* Select Printer */}
            {modal.step === 'select-printer' && (
              <div className="flex flex-col gap-4">
                <h3 className="text-lg font-semibold text-gray-900">Seleziona stampante</h3>
                <div className="space-y-2">
                  {modal.printers.map((printer) => (
                    <label
                      key={printer.printer}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg border-2 cursor-pointer transition-colors ${
                        selectedPrinter === printer.printer
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="printer"
                        value={printer.printer}
                        checked={selectedPrinter === printer.printer}
                        onChange={(e) => setSelectedPrinter(e.target.value)}
                        className="sr-only"
                      />
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        selectedPrinter === printer.printer
                          ? 'border-primary-500 bg-primary-500'
                          : 'border-gray-300'
                      }`}>
                        {selectedPrinter === printer.printer && (
                          <div className="w-2 h-2 rounded-full bg-white" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{printer.printer}</p>
                        <p className="text-xs text-gray-500">{printer.dots} DPI</p>
                      </div>
                      <Printer className="w-4 h-4 text-gray-400" />
                    </label>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handlePrinterConfirm(modal.printers)}
                    className="btn btn-primary text-sm flex-1 flex items-center justify-center gap-2"
                  >
                    <Printer className="w-4 h-4" />
                    Stampa
                  </button>
                  <button
                    onClick={closeModal}
                    className="btn btn-secondary text-sm flex-1 flex items-center justify-center gap-2"
                  >
                    Annulla
                  </button>
                </div>
              </div>
            )}

            {/* Printing */}
            {modal.step === 'printing' && (
              <div className="flex flex-col items-center gap-4 py-6">
                <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
                <p className="text-gray-600">Stampa in corso...</p>
              </div>
            )}

            {/* Success */}
            {modal.step === 'success' && (
              <div className="flex flex-col items-center gap-4 py-4">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <Check className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Stampa completata</h3>
                <p className="text-sm text-gray-600">{modal.message}</p>
              </div>
            )}

            {/* Error */}
            {modal.step === 'error' && (
              <div className="flex flex-col items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <X className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Errore di stampa</h3>
                <p className="text-sm text-gray-600 text-center">{modal.message}</p>
                <div className="bg-gray-50 rounded-lg p-3 w-full">
                  <p className="text-xs text-gray-500">Verifica che:</p>
                  <ul className="text-xs text-gray-600 space-y-0.5 list-disc list-inside mt-1">
                    <li>La stampante sia collegata e accesa</li>
                    <li>Il server di stampa MakeID sia in esecuzione</li>
                  </ul>
                </div>
                <div className="flex gap-2 w-full">
                  <button
                    onClick={handleClick}
                    className="btn btn-primary text-sm flex-1 flex items-center justify-center gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Riprova
                  </button>
                  <button
                    onClick={closeModal}
                    className="btn btn-secondary text-sm flex-1 flex items-center justify-center gap-2"
                  >
                    Chiudi
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Hook to check if we should show the "change printer" option.
 * Returns the saved printer name if any.
 */
export function useSavedPrinter() {
  return getSavedPrinter();
}

/**
 * Button to clear the saved printer selection.
 */
export function ChangePrinterButton() {
  if (!isDesktop()) return null;

  const savedPrinter = getSavedPrinter();
  if (!savedPrinter) return null;

  return (
    <button
      onClick={() => {
        localStorage.removeItem('campflow_selected_printer');
        // Force re-render - the next print will show the printer selection
        window.dispatchEvent(new Event('storage'));
      }}
      className="text-xs text-gray-500 hover:text-gray-700 mt-1 underline cursor-pointer"
    >
      Cambia stampante ({savedPrinter})
    </button>
  );
}
